import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { LMSCourse } from './LMSCourse.js';
import { LMSCourseAttempt } from './LMSCourseAttempt.js';

export enum LMSEnrollmentStatus {
  ASSIGNED = 'assigned',
  IN_PROGRESS = 'in_progress',
  PASSED = 'passed',
  FAILED = 'failed',
}

@Entity()
export class LMSCourseEnrollment extends MongoEntity {
  static override _class = 'LMSCourseEnrollment';
  static override _collectionName = 'lms_course_enrollments';

  @validate(nonOptional())
  @Field(() => String)
  courseId: Maybe<string>;

  @DataLoadedField(() => LMSCourse, enrollment => enrollment.courseId)
  course: Maybe<LMSCourse>;

  @validate(nonOptional())
  @Field(() => String)
  userId: Maybe<string>;

  get status() {
    const attempts = this.attempts ?? [];
    const completedAttempts = attempts.filter(attempt => attempt.completedAt);
    if (completedAttempts.find(attempt => attempt.passed)) return LMSEnrollmentStatus.PASSED;
    if (attempts.find(attempt => !attempt.completedAt)) return LMSEnrollmentStatus.IN_PROGRESS;
    if (completedAttempts.length) return LMSEnrollmentStatus.FAILED;
    return LMSEnrollmentStatus.ASSIGNED;
  }

  @Field(() => Int)
  scorePercent: Maybe<number>;

  @Field(() => Date)
  completedAt: Maybe<Date>;

  @Field(() => [LMSCourseAttempt])
  attempts: Maybe<LMSCourseAttempt[]>;

  @Field(() => Date)
  lockedAt: Maybe<Date>;

  @Field(() => String)
  lockReason: Maybe<string>;

  @Field(() => Date, { default: v => v ?? new Date() })
  createdAt: Maybe<Date>;
}
